/**
 * First-login onboarding, routed at `/welcome` as `<page-onboarding>`.
 *
 * Implements the first-login half of `SC-F910` (`SC-S905`) plus `SC-F004`
 * (passkey registration) at the one moment `concept.md` §6.1 says it converts.
 * `<page-sign-in>` sends an account here when its display name is empty *and*
 * it was created in the last fifteen minutes — the joint condition `SC-S001`
 * states, because `SC-F002` mints every account with an empty name and a
 * name-only test would make this a toll booth on every sign-in.
 *
 * Two steps, both skippable, in this order:
 *
 *   1. **A display name.** `05` §3.1 has it "empty until onboarding"; the shell
 *      falls back to the email address without it, so it is a nicety and never
 *      a wall.
 *   2. **The passkey offer.** `concept.md` §6.1 is precise about why it sits
 *      here: it is offered *before the account has anything to protect*, which
 *      is exactly why people accept it. The copy is written for that moment —
 *      one minute of setup while there is nothing at stake — and not as a
 *      security warning about assets that do not exist yet. It is honest about
 *      `01` §1.1's invariant NL-1 in the same breath: a passkey is an
 *      additional way in and never the only one, emailed codes can never be
 *      turned off, and every passkey on the account can be deleted later
 *      (`SC-F007`) without locking anyone out.
 *
 * The step disappears entirely — rather than failing at the ceremony — on a
 * browser or a non-secure origin that cannot run WebAuthn (`SC-S008`).
 *
 * **The four states.** *Loading* is each in-flight write (the name save, the
 * registration ceremony); *empty* is the untouched name field on a fresh
 * account; *populated* is the same field prefilled from the session record, so
 * arriving here twice means confirming rather than retyping; *error* renders
 * the `apierr` code's copy against the field with the `request_id` underneath.
 *
 * Both exits land on the `?next=` the sign-in flow carried in, or on the
 * instance list — which owns `SC-S907`'s designed zero state and is where the
 * rest of `SC-F910`'s guided flow (create an instance, connect a datasource,
 * author a policy, publish, dry-run) begins.
 */

import { LitElement, css, html, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";

import "../components/index.js";
import { toast } from "../components/index.js";

import { registerPasskey, updateAccount } from "../api/auth.js";
import { pb } from "../api/client.js";
import { parseError, supportReference } from "../api/errors.js";
import {
  devicePasskeyName,
  passkeyFailureMessage,
  webauthnSupported,
} from "../api/webauthn.js";
import { navigate } from "../nav.js";

/** `05` §3.1 caps `users.name` at 100 characters. Checked here so an over-long
 * name is a field message and not a round-trip. */
const NAME_MAX = 100;

@customElement("page-onboarding")
export class PageOnboarding extends LitElement {
  @state() private step: "name" | "passkey" = "name";
  @state() private name = "";
  @state() private submitting = false;
  @state() private fieldError = "";
  /** `request_id` of the last failure, rendered so it can be quoted. */
  @state() private errorRef = "";
  /** The `apierr` code of the last failure. Rendered next to the reference for the
   * same reason every other page renders it: the message is written for the reader
   * and the code is what a support conversation and a log search actually match on,
   * so a screen that shows one and not the other is missing half the handle. Empty
   * for a purely local validation failure, which has no code because it never
   * reached the server. */
  @state() private errorCode = "";

  /** Whether this browser and origin can run a WebAuthn ceremony at all.
   * Computed once — it cannot change while the page is mounted. */
  private readonly passkeySupport = webauthnSupported();

  static override styles = css`
    :host {
      display: flex;
      justify-content: center;
      align-items: flex-start;
      padding-top: clamp(var(--bino-space-lg), 8vh, 6rem);
    }
    /* The surface itself is bn-card; this only sizes it. */
    .card {
      width: 100%;
      max-width: 27rem;
      font-family: var(--bino-font-sans);
    }
    h1 {
      margin: 0 0 var(--bino-space-xs);
      font-size: var(--bino-font-size-lg);
      font-weight: 600;
      color: var(--bino-text);
      letter-spacing: -0.01em;
    }
    p {
      margin: 0 0 var(--bino-space-md);
      font-size: var(--bino-font-size-md);
      line-height: 1.55;
      color: var(--bino-text-secondary);
    }
    .steps {
      margin: 0 0 var(--bino-space-md);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      letter-spacing: 0.04em;
      text-transform: uppercase;
    }
    .fine {
      margin: 0;
      font-size: var(--bino-font-size-sm);
      line-height: 1.55;
      color: var(--bino-text-secondary);
    }
    .actions {
      margin-top: var(--bino-space-lg);
      display: flex;
      flex-direction: column;
      gap: var(--bino-space-sm);
    }
    .ref {
      margin: var(--bino-space-xs) 0 0;
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-xs);
      color: var(--bino-text-secondary);
      /* One click selects the whole reference, which is what a reader pasting
         it into a support ticket is trying to do. */
      user-select: all;
    }
  `;

  override connectedCallback() {
    super.connectedCallback();
    // Prefill from the session record so that arriving here a second time — a
    // browser back, a reload mid-flow — is a confirmation and not a retype.
    const name = pb.authStore.record?.name;
    if (typeof name === "string") this.name = name;
  }

  /** done leaves onboarding for wherever sign-in was originally headed. The
   * open-redirect guard is the same one `<page-sign-in>` applies: `//host` and
   * `/\host` both start with a slash and both leave the origin. */
  private done() {
    const raw = new URLSearchParams(location.search).get("next");
    const safe = raw && raw[0] === "/" && raw[1] !== "/" && raw[1] !== "\\" ? raw : "";
    navigate(safe || "/instances", { replace: true });
  }

  /** advance moves to the passkey offer, or straight out when this browser
   * cannot run the ceremony — offering a button that can only fail would be the
   * dishonest version of `SC-S008`. */
  private advance() {
    if (this.passkeySupport) this.step = "passkey";
    else this.done();
  }

  private failed(err: unknown) {
    const e = parseError(err);
    this.fieldError = e.message;
    this.errorRef = supportReference(e);
    this.errorCode = e.code;
    // A session that evaporated mid-onboarding cannot be recovered by editing
    // the field, so hand it back to the sign-in flow with this page as the
    // return path rather than leaving a form that will fail identically.
    if (e.status === 401) {
      navigate("/sign-in?next=" + encodeURIComponent(location.pathname + location.search), {
        replace: true,
      });
    }
  }

  private async saveName() {
    const name = this.name.trim();
    if (name === "") {
      this.fieldError = "Enter a name, or skip this step.";
      this.errorRef = "";
      this.errorCode = "";
      return;
    }
    if (name.length > NAME_MAX) {
      this.fieldError = `Names are limited to ${NAME_MAX} characters.`;
      this.errorRef = "";
      this.errorCode = "";
      return;
    }
    this.fieldError = "";
    this.errorRef = "";
    this.errorCode = "";
    this.submitting = true;
    try {
      const account = await updateAccount({ name });
      // Keep the cached auth record in step: the token is unchanged, only the
      // record payload gained a name, and the shell renders from that record.
      const record = pb.authStore.record;
      if (record) pb.authStore.save(pb.authStore.token, { ...record, name: account.name });
      this.advance();
    } catch (err) {
      this.failed(err);
    } finally {
      this.submitting = false;
    }
  }

  private async addPasskey() {
    if (this.submitting) return;
    this.fieldError = "";
    this.errorRef = "";
    this.errorCode = "";
    this.submitting = true;
    try {
      await registerPasskey(devicePasskeyName());
      toast("Passkey added — it will sign you in next time.", "success");
      this.done();
    } catch (err) {
      // Dismissing the platform prompt is "let me think about it", so both
      // buttons stay live and nothing is said. Everything else — an origin
      // whose relying-party id does not match, a credential this device has
      // already registered — is named.
      const msg = passkeyFailureMessage(err);
      if (msg) toast(msg, "error");
    } finally {
      this.submitting = false;
    }
  }

  private onKey(e: KeyboardEvent) {
    if (e.key === "Enter" && !this.submitting) {
      e.preventDefault();
      void this.saveName();
    }
  }

  override render() {
    return html`<bn-card class="card">
      ${this.step === "name" ? this.renderName() : this.renderPasskey()}
    </bn-card>`;
  }

  private renderName() {
    const total = this.passkeySupport ? 2 : 1;
    return html`
      <p class="steps">Step 1 of ${total}</p>
      <h1>Welcome to Sluice Cloud</h1>
      <p>
        Your organization is ready and waiting. Before you open it — what should colleagues see next
        to your changes?
      </p>

      <bn-field
        label="Your name"
        required
        error=${this.fieldError}
        hint="Shown on invitations, published generations and the audit trail."
      >
        <bn-input
          id="name"
          autocomplete="name"
          placeholder="Nadja Ritter"
          ?invalid=${this.fieldError !== ""}
          ?disabled=${this.submitting}
          .value=${this.name}
          @input=${(e: CustomEvent<{ value: string }>) => {
            this.name = e.detail.value;
            if (this.fieldError) {
              this.fieldError = "";
              this.errorRef = "";
              this.errorCode = "";
            }
          }}
          @keydown=${(e: KeyboardEvent) => this.onKey(e)}
        ></bn-input>
      </bn-field>
      ${this.fieldError && (this.errorCode || this.errorRef)
        ? html`<p class="ref">
            ${[this.errorCode, this.errorRef].filter((s) => s !== "").join(" · ")}
          </p>`
        : nothing}

      <div class="actions">
        <bn-button
          variant="primary"
          block
          ?loading=${this.submitting}
          @click=${() => void this.saveName()}
        >
          Continue
        </bn-button>
        <bn-button variant="ghost" block ?disabled=${this.submitting} @click=${() => this.advance()}>
          Skip for now
        </bn-button>
      </div>
    `;
  }

  private renderPasskey() {
    return html`
      <p class="steps">Step 2 of 2</p>
      <h1>Set up a passkey while it costs nothing</h1>
      <p>
        There is nothing in your organization yet — no instances, no policies, no audit trail. That
        makes this the cheapest minute you will ever have to decide how you get back in. One tap now,
        and every sign-in after this one is your fingerprint, your face or your device PIN instead of
        a trip to your inbox.
      </p>
      <p class="fine">
        A passkey is an additional way in, never the only one. Emailed codes cannot be switched off
        for any account, and you can delete every passkey you own later under Security without losing
        access — which is precisely why adding one now is safe.
      </p>

      <div class="actions">
        <bn-button
          variant="primary"
          block
          ?loading=${this.submitting}
          @click=${() => void this.addPasskey()}
        >
          Add a passkey to this device
        </bn-button>
        <bn-button variant="ghost" block ?disabled=${this.submitting} @click=${() => this.done()}>
          Not now
        </bn-button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-onboarding": PageOnboarding;
  }
}
